import { FormRule, Switch } from "antd";
import { ReactElement, ReactNode } from "react";
import GuestDetails from "../Pages/HotelDetails/components/GuestDetails/GuestDetails";
import { SearchFormInputs } from "./SearchPage";
import i18n from "src/i18n";
import { track } from "@amplitude/analytics-browser";
import Payment from "../Pages/HotelDetails/Steps/Payment/Payment";
import GuestTextField from "../Pages/HotelDetails/components/GuestDetails/components/GuestTextField/GuestTextField";
import GuestSelectField from "../Pages/HotelDetails/components/GuestDetails/components/GuestSelectField/GuestSelectField";
import type { Guest } from "../Pages/HotelDetails/components/GuestDetails/utils";

type HotelStepType = {
  value: HotelStepValue;
  label: ReactNode;
  inPageTitle?: ReactNode;
  number: number;
  buttonTitle?: ReactNode;
  canEnter?: (state: ValidityState) => boolean;
  canExit?: (state: ValidityState) => boolean;
  inPageSubtitle?: ReactNode;
  buttonAction?: (helpers: { onClick: () => void }) => void;
  Component?: ReactElement;
};

const HOTEL_DETAILS_STEPS: HotelStepType[] = [
  {
    value: "guests",
    get label() {
      return i18n.t("Hotels.booking.guestsDetails");
    },
    get inPageTitle() {
      return i18n.t("Hotels.booking.whosStaying");
    },
    get inPageSubtitle() {
      return i18n.t("Hotels.booking.guestsSubtitle");
    },
    number: 0,
    get buttonTitle() {
      return i18n.t("Hotels.booking.continueToPayment");
    },
    canEnter: (state) => true,
    canExit: (state) => true,
    buttonAction: ({ onClick }) => {
      onClick?.();
      track("Proceeded To Payment");
    },
    Component: <GuestDetails />,
  },
  {
    value: "payment",
    get label() {
      return i18n.t("Hotels.booking.payment");
    },
    get inPageTitle() {
      return null;
    },
    number: 1,
    canEnter: (state) => state.guests === true,
    buttonAction: ({ onClick }) => {
      onClick?.();
      track("Proceeded To Review");
    },
    Component: <Payment />,
  },
];

type HotelStepValue = "guests" | "payment" | "review";

type ValidityState = Record<HotelStepValue, boolean>;

const INITIAL_STEPS_VALIDATION: ValidityState = {
  guests: false,
  payment: true,
  review: true,
};

const HOTEL_DETAILS_STEPS_COUNT = HOTEL_DETAILS_STEPS.length;

const GUEST_TITLE_OPTIONS = [
  { value: "Mr", get label() { return i18n.t("Flights.details.titles.mr"); } },
  { value: "Mrs", get label() { return i18n.t("Flights.details.titles.mrs"); } },
  { value: "Ms", get label() { return i18n.t("Flights.details.titles.ms"); } },
];

const GUEST_NAME_PATTERN = /^[A-Za-z]+$/;
const GUEST_NAME_MIN_LENGTH = 2;
const GUEST_NAME_MAX_LENGTH = 25;

type GuestFormValues = {
  title?: "Mr" | "Mrs" | "Ms";
  firstName?: string;
  lastName?: string;
  isLeadGuest?: boolean;
  contact?: { email?: string; phone?: string };
};

type GuestsInputType = Record<string, GuestFormValues>;

type GuestClassificationType = "adult" | "child";

type GuestFieldContext = {
  guestType: GuestClassificationType;
  guestKey: string;
  form: any;
  conditionFields: {
    isPrimaryGuest: boolean;
    otherGuests: Guest[];
    isLargeScreen: boolean;
  };
};

type GuestDataOptionType = {
  name: string;
  order?: number;
  label?: string | null;
  initialValue?: any;
  component: ReactNode | ((value: GuestFieldContext) => ReactNode);
  rules?: FormRule[] | ((value: GuestFieldContext) => FormRule[]);
  parentPath?: string[];
  valuePropName?: string;
  layout?: "horizontal" | "vertical";
  placement?: "header" | "body";
  fullWidth?: boolean;
};

const normalizeGuestName = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");

const createUniqueGuestNameValidator = ({ form, guestKey, conditionFields }: GuestFieldContext) => ({
  validator: async (_: unknown, __: unknown) => {
    const currentFirstName = normalizeGuestName(form.getFieldValue([guestKey, "firstName"]));
    const currentLastName = normalizeGuestName(form.getFieldValue([guestKey, "lastName"]));
    if (!currentFirstName || !currentLastName) return Promise.resolve();

    const duplicateGuest = conditionFields.otherGuests.find((otherGuest) => {
      const otherFirstName = normalizeGuestName(form.getFieldValue([otherGuest.key, "firstName"]));
      const otherLastName = normalizeGuestName(form.getFieldValue([otherGuest.key, "lastName"]));
      return otherFirstName === currentFirstName && otherLastName === currentLastName;
    });

    return duplicateGuest
      ? Promise.reject(new Error(i18n.t("Hotels.guests.duplicateGuestName", { guestLabel: duplicateGuest.label })))
      : Promise.resolve();
  },
});

const SHARED_GUEST_DATA: GuestDataOptionType[] = [
  {
    name: "title",
    order: 0,
    fullWidth: true,
    get label() {
      return i18n.t("Flights.details.form.title");
    },
    component: () => (
      <GuestSelectField
        options={GUEST_TITLE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
        placeholder={i18n.t("Hotels.guests.selectTitle")}
      />
    ),
    rules: () => [{ required: true, message: i18n.t("Flights.details.form.titleRequired") }],
  },
  {
    name: "firstName",
    order: 1,
    get label() {
      return i18n.t("Hotels.guests.firstName");
    },
    get component() {
      return <GuestTextField placeholder={i18n.t("Hotels.guests.firstNamePlaceholder")} />;
    },
    rules: (context: GuestFieldContext) => [
      { required: true, message: i18n.t("Hotels.guests.firstNameRequired") },
      { min: GUEST_NAME_MIN_LENGTH, max: GUEST_NAME_MAX_LENGTH, message: i18n.t("Hotels.guests.firstNameLength") },
      { pattern: GUEST_NAME_PATTERN, message: i18n.t("Hotels.guests.validFirstName") },
      createUniqueGuestNameValidator(context),
    ],
  },
  {
    name: "lastName",
    order: 2,
    get label() {
      return i18n.t("Hotels.guests.lastName");
    },
    get component() {
      return <GuestTextField placeholder={i18n.t("Hotels.guests.lastNamePlaceholder")} />;
    },
    rules: (context: GuestFieldContext) => [
      { required: true, message: i18n.t("Hotels.guests.lastNameRequired") },
      { min: GUEST_NAME_MIN_LENGTH, max: GUEST_NAME_MAX_LENGTH, message: i18n.t("Hotels.guests.lastNameLength") },
      { pattern: GUEST_NAME_PATTERN, message: i18n.t("Hotels.guests.validLastName") },
      createUniqueGuestNameValidator(context),
    ],
  },
];

// Keeps a horizontal Form.Item's label and switch on one line (antd otherwise stacks them on narrow widths).
const INLINE_SWITCH_CLASSNAME =
  "flex! items-center! [&_.ant-form-item-row]:flex-nowrap! [&_.ant-form-item-row]:items-center! [&_.ant-form-item-label]:flex-none! [&_.ant-form-item-label]:py-0! [&_.ant-form-item-label]:ps-0! [&_.ant-form-item-label]:pe-1.5! [&_.ant-form-item-label>label]:after:hidden! [&_.ant-form-item-control]:flex-none!";

const GUEST_CONTACT_DATA: GuestDataOptionType[] = [
  {
    name: "email",
    get label() {
      return i18n.t("Hotels.guests.emailAddress");
    },
    get component() {
      return <GuestTextField placeholder="jonathan.wick@example.com" className="h-12 rounded-md border-border-default bg-background-default px-4" />;
    },
    parentPath: ["contact"],
    rules: ({ conditionFields }: GuestFieldContext) => [
      { required: !!conditionFields?.isPrimaryGuest, message: i18n.t("Flights.details.form.mainContactEmailRequired") },
      { type: "email", message: i18n.t("Flights.details.form.validEmail") },
    ],
  },
  {
    name: "phone",
    get label() {
      return i18n.t("Flights.details.form.phoneNumber");
    },
    get component() {
      return <GuestTextField placeholder="2025550123" className="h-12 rounded-md border-border-default bg-background-default px-4" />;
    },
    parentPath: ["contact"],
    rules: ({ conditionFields }: GuestFieldContext) => [
      { required: !!conditionFields?.isPrimaryGuest, message: i18n.t("Flights.details.form.mainContactPhoneRequired") },
      { pattern: /^\d{7,17}$/, message: i18n.t("Flights.details.form.validPhoneNumber") },
    ],
  },
];

const GUEST_DATA_MAP: Record<GuestClassificationType, GuestDataOptionType[]> = {
  adult: [
    ...SHARED_GUEST_DATA,
    ...GUEST_CONTACT_DATA,
    {
      name: "isLeadGuest",
      get label() {
        return i18n.t("Hotels.guests.setAsPrimaryGuest");
      },
      layout: "horizontal",
      placement: "header",
      valuePropName: "checked",
      className: INLINE_SWITCH_CLASSNAME,
      component: (context: GuestFieldContext) => {
        const otherAdultGuests = context.conditionFields.otherGuests.filter((guest) => guest.type === "adult");
        if (otherAdultGuests.length === 0) return null;
        return (
          <Switch
            onChange={(checked) => {
              if (!checked) {
                context.form.setFieldValue([context.guestKey, "isLeadGuest"], true);
                return;
              }
              otherAdultGuests.forEach((guest) => {
                context.form.setFieldValue([guest.key, "isLeadGuest"], false);
              });
              context.form.setFieldValue([context.guestKey, "isLeadGuest"], true);
            }}
          />
        );
      },
      rules: [],
    } as GuestDataOptionType,
  ],
  child: [...SHARED_GUEST_DATA],
};

type GuestSectionConfig = {
  title: (conditionFields?: GuestFieldContext["conditionFields"]) => ReactNode;
  subtitle: (conditionFields?: GuestFieldContext["conditionFields"]) => ReactNode;
  inputTypes: Partial<Record<GuestClassificationType, string[]>>;
  className?: string;
  columns?: number;
};

const GUEST_SECTIONS: Record<string, GuestSectionConfig> = {
  mainSection: {
    title: () => i18n.t("Flights.details.form.personalInformation"),
    subtitle: () => null,
    inputTypes: {
      adult: ["title", "firstName", "lastName"],
      child: ["title", "firstName", "lastName"],
    },
  },
  contactSection: {
    title: () => i18n.t("Flights.details.form.contactDetails"),
    subtitle: (conditionFields?: GuestFieldContext["conditionFields"]) =>
      conditionFields?.isPrimaryGuest ? <div>{i18n.t("Hotels.guests.contactDetailsNotice")}</div> : null,
    inputTypes: {
      adult: ["email", "phone"],
      child: [],
    },
    className: "bg-background-minimal!",
  },
};

type PaymentState = {
  pointsToPay: number;
  cashToPay: number;
  selection: "PayAll" | "PayCustom";
};

const buildInitialPrimaryGuestKey = (rooms: SearchFormInputs["rooms"]): string | null => {
  const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
  return totalAdults > 0 ? "room_0_adult_0" : null;
};

export {
  HOTEL_DETAILS_STEPS_COUNT,
  INITIAL_STEPS_VALIDATION,
  HOTEL_DETAILS_STEPS,
  GUEST_TITLE_OPTIONS,
  GUEST_NAME_PATTERN,
  GUEST_NAME_MIN_LENGTH,
  GUEST_NAME_MAX_LENGTH,
  buildInitialPrimaryGuestKey,
  GUEST_DATA_MAP,
  GUEST_SECTIONS,
};
export type {
  ValidityState,
  HotelStepType,
  HotelStepValue,
  GuestFormValues,
  GuestsInputType,
  PaymentState,
  GuestClassificationType,
  GuestFieldContext,
  GuestDataOptionType,
};
